import React, { useRef, useEffect } from 'react'

/**
 * PhoneNumberInput component for formatting and handling US phone numbers
 * Formats phone numbers as "(123) 456-7890" while typing
 * Matches behavior of phone_number_input_formatter.js
 */
const PhoneNumberInput = ({
  label,
  value,
  onChange,
  normalSize = false,
  className = '',
  style = {},
  ...props
}) => {
  const inputRef = useRef(null)

  /**
   * Checks if the input is a numeric character
   * @param {Event} event - Keyboard event
   * @returns {boolean} True if input is numeric
   */
  const isNumericInput = event => {
    return /^\d$/.test(String.fromCharCode(event.keyCode || event.which))
  }

  /**
   * Checks if the key pressed is a modifier key (allowed non-numeric input)
   * @param {Event} event - Keyboard event
   * @returns {boolean} True if key is a modifier key
   */
  const isModifierKey = event => {
    const key = event.key
    const ctrl = event.ctrlKey || event.metaKey

    // allowed keys
    const functionKeys = ['Backspace', 'Tab', 'Enter', 'Home', 'End', 'Delete']
    const arrowKeys = ['ArrowLeft', 'ArrowUp', 'ArrowRight', 'ArrowDown']
    const ctrlCombos = ['a', 'c', 'v', 'x', 'z']

    return (
      event.shiftKey ||
      functionKeys.includes(key) ||
      arrowKeys.includes(key) ||
      (ctrl && ctrlCombos.includes(key.toLowerCase()))
    )
  }

  /**
   * Enforces format by preventing invalid input
   * @param {Event} event - Keyboard event
   */
  const enforceFormat = event => {
    if (!isNumericInput(event) && !isModifierKey(event)) {
      event.preventDefault()
    }
  }

  /**
   * Formats the input as a phone number
   * @param {Event} event - Keyboard event
   */
  const formatToPhone = event => {
    if (isModifierKey(event)) {
      return
    }

    const input = event.target.value.replace(/\D/g, '').slice(0, 10)
    const match = input.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/)

    if (!match) return

    const [, areaCode, middle, last] = match

    let formatted = ''
    if (last) formatted = `(${areaCode}) ${middle}-${last}`
    else if (middle) formatted = `(${areaCode}) ${middle.length == 3 ? middle + '-' : middle}`
    else if (areaCode) formatted = `(${areaCode.length == 3 ? areaCode + ') ' : areaCode}`

    event.target.value = formatted

    // Update parent component with raw value
    onChange({
      target: {
        name: event.target.name,
        value: input, // Pass the raw digits to the parent
      },
    })
  }

  /**
   * Handles input changes
   * @param {Event} e - Change event
   */
  const handleChange = e => {
    // The actual formatting is handled by keyup event
    // This is just to handle paste events or other non-keyboard inputs
    const rawValue = e.target.value.replace(/\D/g, '').slice(0, 10)

    onChange({
      target: {
        name: e.target.name,
        value: rawValue,
      },
    })
  }

  // Update the displayed value when the value prop changes
  useEffect(() => {
    if (inputRef.current) {
      const formattedValue = formatToPhoneDisplay(value)
      if (inputRef.current.value !== formattedValue) {
        inputRef.current.value = formattedValue
      }
    }
  }, [value])

  return (
    <div className='position-relative' style={{ ...style }}>
      {label && (
        <small
          className='position-absolute text-muted'
          style={{
            fontSize: '0.7rem',
            top: '2px',
            left: '8px',
            pointerEvents: 'none',
            zIndex: 1,
          }}
        >
          {label}
        </small>
      )}
      <input
        ref={inputRef}
        type='text'
        defaultValue={formatToPhoneDisplay(value)}
        onChange={handleChange}
        onKeyDown={enforceFormat}
        onKeyUp={formatToPhone}
        className={`form-control ${!normalSize ? 'form-control-sm' : ''} ${className}`}
        style={
          !normalSize
            ? {
                paddingTop: label ? '18px' : '4px',
                paddingBottom: '4px',
                ...style,
              }
            : { ...style }
        }
        {...props}
      />
    </div>
  )
}

/**
 * Helper function to format a phone number for display
 * @param {string} value - Raw phone number input (digits only)
 * @returns {string} Formatted phone number
 */
const formatToPhoneDisplay = value => {
  if (!value) return ''

  const input = value.replace(/\D/g, '').slice(0, 10)
  const match = input.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/)

  if (!match) return ''

  const [, areaCode, middle, last] = match

  let formatted = ''
  if (last) formatted = `(${areaCode}) ${middle}-${last}`
  else if (middle) formatted = `(${areaCode}) ${middle.length == 3 ? middle + '-' : middle}`
  else if (areaCode) formatted = `(${areaCode.length == 3 ? areaCode + ') ' : areaCode}`

  return formatted
}

export default PhoneNumberInput
